/**
 * 社外依頼の右パネル(幅 520)。文面の編集・送信・状態の前進・送信記録。
 * 旧 Drawer の中身をそのまま載せ替えた(操作と文言は変えない)。
 *
 * 依頼は **ID で 1 件取得する**(`GET /api/hr/external-requests/{id}`。fix1 中2)。一覧の
 * いまのページに無い依頼(申請詳細の「文面」から来た古い依頼)も開け、送信して今のビューから
 * 消えても送信記録を出し続ける。
 *
 * 本文は「手で直した文面(draft)」だけを state に持ち、未編集なら取得した本文をそのまま出す
 * (fix2 中-1)。再取得・雛形の作り直しで本文が変わったら未編集の欄にはそれが入り、編集中の入力は
 * 消さない。作り直し・保存の応答は ID 別キャッシュへ書き込むので、別の依頼へ切り替えている間に
 * 返っても、開き直したときに古い本文を出して上書き保存させることはない。
 * 書き込む前に、その ID の進行中の 1 件取得を止める(fix3 中-1)。非表示の間の invalidate は
 * 取り直さないので、開き直したときに始まった取得(古い本文を返す)が残っていると、後から届いて
 * キャッシュと未編集の欄を古い本文へ戻し、保存でサーバーの新しい本文を上書きしてしまう。
 * パネルは依頼ごとに `key` で作り直される(ExternalRequestsPage)。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Input, Popconfirm, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";

import type { MailLogRow, WfExternalRequest } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { KeyValueList, PanelSection, SidePanel } from "../../ui";
import { nextExternalStatus } from "./constants";
import { errorText } from "./hooks";
import { employeeLink, externalStatusTag, requestLink } from "./wfBits";
import { useInvalidateWf, useMailLog, useWfPerms } from "./wfHooks";

const MAIL_STATUS: Record<string, { label: string; color: string }> = {
  sent: { label: "送信", color: "green" },
  skipped: { label: "送らず記録", color: "gold" },
  failed: { label: "失敗", color: "red" },
  sending: { label: "送信中", color: "blue" },
};

const logColumns: ColumnsType<MailLogRow> = [
  { title: "日時", width: 180, render: (_: unknown, r) => jst(r.created_at) },
  {
    title: "結果",
    width: 110,
    render: (_: unknown, r) => (
      <Tag color={MAIL_STATUS[r.status]?.color ?? "default"}>{MAIL_STATUS[r.status]?.label ?? r.status}</Tag>
    ),
  },
  { title: "宛先", width: 240, dataIndex: "to_addr", },
  { title: "備考", width: 320, dataIndex: "error", ellipsis: true },
];

export function ExternalRequestPanel({ id, onClose }: { id: number; onClose: () => void }) {
  const api = useApi();
  // キーは ["hr", …] の下(送信・保存の後の invalidate で取り直す)
  const qc = useQueryClient();
  const oneKey = (eid: number) => ["hr", "wf-external", "one", eid];
  const one = useQuery({
    queryKey: oneKey(id),
    queryFn: () => api.externalRequest(id),
  });
  const row: WfExternalRequest | undefined = one.data;
  const invalidate = useInvalidateWf();
  const { canOperate } = useWfPerms();
  const logs = useMailLog(row?.id);
  // 手で直した文面。null は未編集(取得した本文をそのまま出し、再取得の新しい本文も入る)
  const [draft, setDraft] = useState<string | null>(null);
  const body = draft ?? row?.body ?? "";

  const send = useMutation({
    mutationFn: (eid: number) => api.sendExternalRequest(eid),
    onSuccess: (res) => {
      void message.success(res.sent ? `送信した(${res.to})` : `送らずに記録した: ${res.message ?? ""}`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  const advance = useMutation({
    mutationFn: ({ eid, next }: { eid: number; next: string }) => api.setExternalStatus(eid, next),
    onSuccess: (r) => {
      void message.success(`#${r.id} を「${r.status_label}」にした`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  const saveBody = useMutation({
    mutationFn: ({ eid, text }: { eid: number; text: string }) => api.updateExternalRequest(eid, { body: text }),
    onSuccess: async (r, { text }) => {
      // 進行中の 1 件取得(保存前の本文を返しうる)を止めてから書き込む(fix3 中-1)
      await qc.cancelQueries({ queryKey: oneKey(r.id), exact: true });
      qc.setQueryData(oneKey(r.id), r);
      // 保存した文面のままなら未編集に戻す(保存中に書き足した分は残す)
      if (r.id === id) setDraft((d) => (d === text ? null : d));
      void message.success("文面を保存した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  /** 件名・本文を雛形から作り直す。**手で直した文面は消える**。送信後は 409。 */
  const rerender = useMutation({
    mutationFn: ({ eid, lang }: { eid: number; lang: string }) => api.rerenderExternalRequest(eid, lang),
    onSuccess: async (r) => {
      // 進行中の 1 件取得(作り直す前の本文を返しうる)を止めてから書き込む(fix3 中-1)
      await qc.cancelQueries({ queryKey: oneKey(r.id), exact: true });
      // 新しい本文は ID 別キャッシュへ(切り替え中に返っても、A を開き直せば作り直した本文が出る)
      qc.setQueryData(oneKey(r.id), r);
      // この依頼を開いているときだけ手書きを捨てて新しい本文を出す(別の依頼の本文には触らない)
      if (r.id === id) setDraft(null);
      void message.success("雛形から作り直した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (!row) {
    return (
      <SidePanel title={`社外依頼 #${id}`} onClose={onClose} width={520} loading={one.isPending}>
        {one.isError && (
          <Alert type="error" showIcon message={`社外依頼 #${id} を開けなかった`} description={errorText(one.error)} />
        )}
      </SidePanel>
    );
  }
  const current = row;
  const next = nextExternalStatus(current.status);
  const editable = canOperate && current.status === "draft";

  return (
    <SidePanel
      title={current.partner_name}
      ariaLabel={`社外依頼 #${current.id} の詳細`}
      status={externalStatusTag(current)}
      sub={
        <Space size={6} wrap>
          <span>#{current.id}</span>
          {requestLink(current.request_id, current.request_kind)}
          <span>{employeeLink(current.employee_id, current.employee_name)}</span>
        </Space>
      }
      fullHref={`/hr/wf-requests/${current.request_id}?tab=external`}
      width={520}
      onClose={onClose}
      barActions={
        editable ? (
          <Button type="primary" size="small" loading={send.isPending} onClick={() => send.mutate(current.id)}>
            送信する
          </Button>
        ) : canOperate && current.send_unknown ? (
          <Button type="primary" size="small" loading={send.isPending} onClick={() => send.mutate(current.id)}>
            再送
          </Button>
        ) : canOperate && next ? (
          <Button size="small" loading={advance.isPending} onClick={() => advance.mutate({ eid: current.id, next: next.value })}>
            「{next.label}」にする
          </Button>
        ) : undefined
      }
    >
      <PanelSection title="依頼">
        <KeyValueList
          narrow
          items={[
            { label: "宛先", value: current.partner_email || "", copyable: true, always: true },
            { label: "向き", value: current.action_label },
            { label: "依頼項目", value: current.items.join(" / "), wide: true },
            { label: "件名", value: current.subject, wide: true },
            { label: "送信日時", value: current.sent_at ? jst(current.sent_at) : "" },
            { label: "報告URLの期限", value: current.token_expires_at ? jst(current.token_expires_at) : "" },
            { label: "先方の報告", value: current.reported_note, wide: true },
          ]}
        />
      </PanelSection>
      <PanelSection title="本文">
        <Input.TextArea
          style={{ fontFamily: "monospace" }}
          rows={12}
          value={body}
          onChange={(e) => setDraft(e.target.value)}
          disabled={!editable}
          aria-label="本文"
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          完了報告URLは送信時に本文の末尾へ足す(ここには出さない)。送信後は履歴なので直せない。
        </Typography.Text>
        {editable && (
          <Space wrap style={{ marginTop: 8 }}>
            <Button size="small" loading={saveBody.isPending} onClick={() => saveBody.mutate({ eid: current.id, text: body })}>
              文面を保存
            </Button>
            <Popconfirm
              title="英語の雛形で作り直す"
              description="件名と本文を英語の雛形から作り直す。手で直した文面は消える"
              okText="作り直す"
              cancelText="やめる"
              onConfirm={() => rerender.mutate({ eid: current.id, lang: "en" })}
            >
              <Button size="small" loading={rerender.isPending}>
                英語の雛形で作り直す
              </Button>
            </Popconfirm>
            <Popconfirm
              title="日本語の雛形で作り直す"
              description="件名と本文を日本語の雛形から作り直す。手で直した文面は消える"
              okText="作り直す"
              cancelText="やめる"
              onConfirm={() => rerender.mutate({ eid: current.id, lang: "ja" })}
            >
              <Button size="small">日本語に戻す</Button>
            </Popconfirm>
          </Space>
        )}
      </PanelSection>
      <PanelSection title="送信記録">
        <Table<MailLogRow>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          columns={logColumns}
          dataSource={logs.data?.items ?? []}
          loading={logs.isFetching}
          pagination={false}
          locale={{ emptyText: "まだ送っていない" }}
        />
      </PanelSection>
    </SidePanel>
  );
}
